import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { modalConfig, ModalEnum } from 'src/app/core/util/modalConfig';
import { PreviewGeoJsonModalComponent } from '../preview-geo-json-modal/preview-geo-json-modal.component';
import { showFeedbackError } from 'src/app/core/util/notification';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-select-geo-json-file-modal',
  templateUrl: './select-geo-json-file-modal.component.html',
  styleUrl: './select-geo-json-file-modal.component.sass',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectGeoJsonFileModalComponent {
  selectedType: 'json' | 'excel' = 'excel';
  errorMessage: boolean = false;
  isDragging = false;
  isUploading = signal(false);
  uploadedFile: File | null = null;
  uploadedFileName = '';

  // eslint-disable-next-line
  constructor(private dialog: MatDialog, private dialogRef: MatDialogRef<SelectGeoJsonFileModalComponent>, @Inject(MAT_DIALOG_DATA) public data:any, private cdRef: ChangeDetectorRef, private translocoService: TranslocoService ){}

  get isOtherTypeDisabled(): boolean {
    return this.uploadedFile !== null;
  }

  setImportType(type: 'json' | 'excel') {
    this.selectedType = type;
  }

  close(){
    this.dialogRef.close();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
    const file = event.dataTransfer?.files?.[0];
    this.validateAndSetFile(file);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.validateAndSetFile(file);
  }

  validateAndSetFile(file?: File) {
    if (!file) return;

    const isJson = this.selectedType === 'json' && (file.name.endsWith('.json') || file.name.endsWith('.geojson'));
    const isExcel = this.selectedType === 'excel' && (file.name.endsWith('.xls') || file.name.endsWith('.xlsx'));


    if (isJson || isExcel) {
      this.uploadedFile = file;
      this.uploadedFileName = file.name;
      if (isJson) {
        if (file.size > 100 * 1024 * 1024) { // 100MB
          showFeedbackError(null, 'File size exceeds 100 MB limit.');
          this.uploadedFile = null;
          this.uploadedFileName = '';
          return;
        }
        // Preview streaming happens in the preview modal (no full-file parse here).
        const input = document.getElementById('fileInput') as HTMLInputElement;
        if (input) input.value = '';
        this.cdRef.detectChanges();
      }
    } else if (file) {
      showFeedbackError('Please select a valid .geojson,.xlsx,.xls file!');
    }
  }

  unselectFile(event: MouseEvent){
    event.stopPropagation();
    this.removeFile();
  }

  removeFile() {
    this.uploadedFile = null;
    this.uploadedFileName = '';
    const input = document.getElementById('fileInput') as HTMLInputElement;
    if (input) input.value = '';
  }

  downloadTemplate(file: string){
    let finalFileName = '';
    
    // Check if it's an Excel file and adjust based on language
    const currentLang = this.translocoService.getActiveLang();
    if (file === 'excel') {
      if (currentLang === 'nl') {
        finalFileName = 'excel_sjabloon.xlsx';
      } else {
        finalFileName = 'excel_template.xlsx';
      }
    } else if (file === 'geojson') {
      if (currentLang === 'nl') {
        finalFileName = 'geojson_sjabloon.geojson';
      } else {
        finalFileName = 'geojson_template.geojson';
      }
    }
    
    const fileUrl = `assets/import_templates/${finalFileName}`;
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = finalFileName;
    link.click();
  }

  openGeoJsonPreviewModal(){
    this.dialogRef.close();
    const dialog = this.dialog.open(PreviewGeoJsonModalComponent, modalConfig({
      data: { 
        file: this.uploadedFile, 
        fileType: this.selectedType, 
        error: this.errorMessage, 
        geojsonPolygons: [],
        stakeholders: [],
        onImportSuccess: this.data?.onImportSuccess
      },
      panelClass: 'customized-dialog-container',
    }, ModalEnum.ModalMedium));
  }
}
